import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import Select from "react-select";
import '../CSS/Global.css';
import { useSidebar } from '../SidebarContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const statusOptions = ['Accepted', 'Rejected', 'Published', 'Under Review'];

const getStatusClass = (status) => {
    switch (status) {
        case 'Accepted':
            return 'status-accepted';
        case 'Rejected':
            return 'status-rejected';
        case 'Published':
            return 'status-published';
        case 'Under Review':
            return 'status-under-review';
        default:
            return '';
    }
};

const Articles = () => {
    const { isSidebarOpen } = useSidebar();
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const cardsPerPage = 4;
    const [searchQuery, setSearchQuery] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState('');
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [fileToDeleteIndex, setFileToDeleteIndex] = useState(null);
    const [paymentFilter, setPaymentFilter] = useState("All");
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isDownloadConfirmOpen, setIsDownloadConfirmOpen] = useState(false);
    const [pendingDownloadData, setPendingDownloadData] = useState(null);
    const [isArticleDeleteOpen, setIsArticleDeleteOpen] = useState(false);
    const [articleToDelete, setArticleToDelete] = useState(null);
    const [volumeOptions, setVolumeOptions] = useState([]);
    const [issueOptions, setIssueOptions] = useState([]);
    const [editingNameId, setEditingNameId] = useState(null);
    const [editedName, setEditedName] = useState("");
    const [savingName, setSavingName] = useState(false);


    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    // Filtered cards
    const filteredCards = cards.filter(card => {
        const query = (searchQuery || "").toLowerCase();

        const details = card?.manuscriptDetails || {};

        const payStatus = (details.pay_status || "").toLowerCase();

        const matchesSearch = (
            (details.title || details.articleTitle || '').toLowerCase().includes(query) ||
            (details.author || details.name || '').toLowerCase().includes(query) ||
            (details.category || details.articleCategory || '').toLowerCase().includes(query) ||
            (details.referenceNumber || '').toLowerCase().includes(query) ||
            (details.paymentStatus || '').toLowerCase().includes(query)
        );

        const matchesPayment =
            paymentFilter === "All" ||
            (paymentFilter === "Paid" && payStatus === "paid") ||
            (paymentFilter === "Unpaid" && payStatus === "unpaid");

        let createdDateStr = "";
        if (details.createdAt) {
            const d = new Date(details.createdAt);
            if (!isNaN(d.getTime())) {
                createdDateStr = d.toISOString().slice(0, 10);
            }
        }

        const start = startDate || '';
        const end = endDate || '';

        const matchesDate = (() => {
            if (start && end && start === end) {
                return createdDateStr === start;
            } else if (start && end) {
                return createdDateStr >= start && createdDateStr <= end;
            } else if (start) {
                return createdDateStr >= start;
            } else if (end) {
                return createdDateStr <= end;
            }
            return true;
        })();

        return matchesSearch && matchesPayment && matchesDate;
    });

    let totalPages =
        filteredCards.length === 0
            ? 0
            : Math.ceil(filteredCards.length / cardsPerPage);

    useEffect(() => {
        if (totalPages === 0) {
            setCurrentPage(1);
            return;
        }

        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [totalPages]);



    const indexOfLastCard = currentPage * cardsPerPage;
    const indexOfFirstCard = indexOfLastCard - cardsPerPage;
    const currentCards = filteredCards.slice(indexOfFirstCard, indexOfLastCard);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleEyeClick = (card) => {
        setSelectedArticle(card);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedArticle(null);
    };

    const handleStatusChange = (e, card) => {
        setPendingStatus(e.target.value);
        setSelectedArticle(card);
        setIsConfirmModalOpen(true);
    };

    useEffect(() => {
        const fetchManuscripts = async () => {
            try {
                const response = await fetch(`https://backend.impactjournal.co.in/processingfee/merged`);
                if (!response.ok) {
                    throw new Error("Failed to fetch manuscripts");
                }
                const data = await response.json();
                setCards(data.reverse());
            } catch (error) {
                console.error("Error fetching manuscripts:", error);
                toast.error("Failed to load manuscripts");
            } finally {
                setLoading(false);
            }
        };

        fetchManuscripts();
    }, []);

    const confirmStatusChange = async () => {
        const refNo = selectedArticle?.manuscriptDetails?.referenceNumber;

        if (!refNo) {
            toast.error("Reference number not found.");
            setIsConfirmModalOpen(false);
            setPendingStatus('');
            setSelectedArticle(null);
            return;
        }

        try {
            const response = await fetch(`https://backend.impactjournal.co.in/manuscript/update-status/${refNo}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ statusOptions: pendingStatus }),
            });

            const result = await response.json();

            if (response.ok) {
                const updated = cards.map((card) => {
                    if (card.manuscriptDetails?.referenceNumber === refNo) {
                        return {
                            ...card,
                            manuscriptDetails: {
                                ...card.manuscriptDetails,
                                statusOptions: pendingStatus
                            }
                        };
                    }
                    return card;
                });

                if (
                    selectedArticle?.manuscriptDetails?.statusOptions === "Published" &&
                    pendingStatus !== "Published" &&
                    selectedArticle?.manuscriptDetails?.certificate
                ) {
                    await fetch(`https://backend.impactjournal.co.in/manuscript/delete-certificate/${refNo}`, {
                        method: "DELETE",
                    });

                    updated.forEach((card) => {
                        if (card.manuscriptDetails?.referenceNumber === refNo) {
                            card.manuscriptDetails.certificate = null;
                        }
                    });
                }

                setCards(updated);
                toast.success(`Status updated to ${pendingStatus}`);
            } else {
                toast.error(result.message || "Failed to update status.");
            }
        } catch (err) {
            toast.error("Server error. Please try again.");
        } finally {
            setIsConfirmModalOpen(false);
            setPendingStatus('');
            setSelectedArticle(null);
        }
    };

    const cancelFileDelete = () => {
        setIsDeleteConfirmOpen(false);
        setFileToDeleteIndex(null);
    };

    const confirmFileDelete = async () => {
        const refNo = selectedArticle?.manuscriptDetails?.referenceNumber;

        if (!refNo) {
            toast.error("Reference number not found.");
            setIsDeleteConfirmOpen(false);
            setFileToDeleteIndex(null);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`https://backend.impactjournal.co.in/manuscript/delete-certificate/${refNo}`, {
                method: "DELETE",
            });

            const result = await response.json();

            if (response.ok) {
                const updated = [...cards];
                if (updated[fileToDeleteIndex]) {
                    updated[fileToDeleteIndex] = {
                        ...updated[fileToDeleteIndex],
                        certificate: null,
                        manuscriptDetails: {
                            ...updated[fileToDeleteIndex].manuscriptDetails,
                            certificate: null
                        }
                    };
                }
                setCards(updated);

                const uploadedCopy = { ...uploadedFiles };
                delete uploadedCopy[fileToDeleteIndex];
                setUploadedFiles(uploadedCopy);

                toast.success("Certificate deleted successfully");
            }
            else {
                toast.error(result.message || "Failed to delete certificate");
            }
        } catch (err) {
            toast.error("Server error during certificate deletion");
        } finally {
            setLoading(false);
            setIsDeleteConfirmOpen(false);
            setFileToDeleteIndex(null);
        }
    };

    const downloadExcel = (dataToExport) => {
        const formattedData = dataToExport.map((card) => {
            const m = card.manuscriptDetails || {};
            return {
                Date: m.createdAt ? new Date(m.createdAt).toLocaleString() : "",
                ReferenceNumber: m.referenceNumber || "",
                Name: m.name || "",
                Email: m.email || "",
                Phone: m.phone || "",
                Country: m.country || "",
                Address: m.address || "",
                ArticleTitle: m.articleTitle || "",
                ArticleCategory: m.articleCategory || "",
                Status: m.statusOptions || "",
                PaymentStatus: m.pay_status || "",
                Amount: card.fee || "",
                File: m.file ? `https://backend.impactjournal.co.in/uploads/manuscripts/${m.file}` : "",
                Certificate: m.certificate ? `https://backend.impactjournal.co.in/uploads/manuscripts/${m.certificate}` : "",
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Manuscripts");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "manuscripts_export.xlsx");
        toast.success("Excel downloaded!");
    };

    const handleDownloadExcel = () => {
        if (filteredCards.length === 0) {
            toast.info("No records found to download.");
            return;
        }

        if (!startDate && !endDate) {
            setPendingDownloadData(filteredCards);
            setIsDownloadConfirmOpen(true);
            return;
        }

        downloadExcel(filteredCards);
    };


    const deleteArticle = async () => {
        if (!articleToDelete?.manuscriptDetails?.id) {
            toast.error("Invalid article selected");
            return;
        }
        console.log(articleToDelete)
        const id = articleToDelete.manuscriptDetails.id;
        const feeid = articleToDelete.id
        console.log(id, feeid)

        try {
            const response = await fetch(`https://backend.impactjournal.co.in/manuscript/${id}/${feeid}`, {
                method: "DELETE",
            });

            const res = await response.json();

            if (response.ok) {
                toast.success("Article deleted successfully");

                // Remove from UI
                setCards(prev =>
                    prev.filter(c => c.manuscriptDetails.id !== id)
                );
            } else {
                toast.error(res.message || "Failed to delete");
            }
        } catch (err) {
            toast.error("Server error while deleting");
        } finally {
            setIsArticleDeleteOpen(false);
            setArticleToDelete(null);
        }
    };



    useEffect(() => {
        const fetchDataPlan = async () => {
            try {
                const response = await fetch(`https://backend.impactjournal.co.in/volume`);
                const res = await response.json();

                const options = res.map(v => ({
                    value: v.id,
                    label: v.volume_name
                }));

                setVolumeOptions(options);
            } catch (error) {
                console.log(error);
            }
        };

        const fetchDataIssue = async () => {
            try {
                const response = await fetch(`https://backend.impactjournal.co.in/issue`);
                const res = await response.json();

                const options = res.map(v => ({
                    value: v.id,
                    label: v.issue
                }));

                setIssueOptions(options);
            } catch (error) {
                console.log(error);
            }
        };

        fetchDataPlan();
        fetchDataIssue();
    }, []);


    const updateAuthorName = async (manuscriptId) => {
        if (!editedName.trim()) {
            toast.error("Name cannot be empty");
            return;
        }

        setSavingName(true);

        try {
            const res = await fetch(
                `https://backend.impactjournal.co.in/manuscript/${manuscriptId}/name`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name: editedName }),
                }
            );


            const result = await res.json();

            if (res.ok) {
                toast.success("Author name updated");

                setCards(prev =>
                    prev.map(card =>
                        card.manuscriptDetails?.id === manuscriptId
                            ? {
                                ...card,
                                manuscriptDetails: {
                                    ...card.manuscriptDetails,
                                    name: editedName,
                                },
                            }
                            : card
                    )
                );

                setEditingNameId(null);
                setEditedName("");
            } else {
                toast.error(result.message || "Failed to update name");
            }
        } catch (err) {
            toast.error("Server error");
        } finally {
            setSavingName(false);
        }
    };


    return (
        <>
            <div className={`open-close ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="articles-impact">
                    <h1 className="dashboard-title">Articles</h1>

                    <div className="filter-search" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="filter-search-input" style={{ flex: 1, marginRight: '16px' }}>
                            <input
                                type="search"
                                placeholder="Search by title, name, category, ref no, status..."
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                            <i className="fa fa-search"></i>
                        </div>

                        <div className="filter-payment-dropdown ">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                                <button className='oms-add-btn mainbuttoncheck' onClick={handleDownloadExcel}>
                                    <i className="fa fa-download"></i> Excel
                                </button>
                            </div>

                            <select
                                value={paymentFilter}
                                onChange={(e) => setPaymentFilter(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="All">All</option>
                                <option value="Paid">Paid</option>
                                <option value="Unpaid">Unpaid</option>
                            </select>
                        </div>
                    </div>

                    <div className="articles-impact-cards">
                        {currentCards.map((card, index) => {
                            const globalIndex = indexOfFirstCard + index;
                            const m = card.manuscriptDetails;

                            if (!m) return null;

                            const isPaid = (m.pay_status || '').toLowerCase() === 'paid';

                            return (
                                <div
                                    key={globalIndex}
                                    className={`article-impact-card ${isPaid ? 'paid' : 'unpaid'}`}
                                >
                                    <div className="article-impact-card-header">
                                        <p>{m.referenceNumber}</p>
                                        <p className={`payment-status ${(m.pay_status || '').toLowerCase()}`}>
                                            {m.pay_status || 'unpaid'} - ₹{card.fee}
                                        </p>
                                    </div>

                                    <div className="article-impact-card-body">
                                        <div>
                                            <div className="article-impact-body-header">
                                                <div className="article-impact-body-category">
                                                    <p>{m.articleCategory}</p>
                                                </div>
                                                <div className="article-impact-body-header-icons">
                                                    <i
                                                        className="fa fa-download"
                                                        title="Download File"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            if (!m.file) {
                                                                toast.info("No file uploaded.");
                                                                return;
                                                            }
                                                            const fileUrl = `https://backend.impactjournal.co.in/uploads/manuscripts/${m.file}`;
                                                            const link = document.createElement('a');
                                                            link.href = fileUrl;
                                                            link.target = "_blank";
                                                            link.download = m.file;
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                        }}
                                                    ></i>

                                                    <i
                                                        className="fa fa-phone"
                                                        title={`Call ${m.phone}`}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => m.phone && (window.location.href = `tel:${m.phone}`)}
                                                    ></i>

                                                    <i
                                                        className="fa fa-envelope"
                                                        title={`Email ${m.email}`}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => m.email && (window.location.href = `mailto:${m.email}`)}
                                                    ></i>

                                                    <i
                                                        className="fa fa-eye"
                                                        title="View Details"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleEyeClick(card)}
                                                    ></i>
                                                </div>
                                            </div>

                                            <div className="article-impact-body-title">
                                                <h2>{m.articleTitle}</h2>
                                            </div>

                                            <div className="article-author-edit">
                                                {editingNameId === m.id ? (
                                                    <>
                                                        <input
                                                            type="text"
                                                            value={editedName}
                                                            onChange={(e) => setEditedName(e.target.value)}
                                                            className="edit-name-input"
                                                        />

                                                        <button
                                                            className="articleauthorsave-btn"
                                                            disabled={savingName}
                                                            onClick={() => updateAuthorName(m.id)}
                                                        >
                                                            {savingName ? "Saving..." : "Save"}
                                                        </button>

                                                        <button
                                                            className="articleauthorcancel-btn"
                                                            onClick={() => {
                                                                setEditingNameId(null);
                                                                setEditedName("");
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <h4>{m.name}</h4>
                                                        <i
                                                            className="fa fa-edit edit-icon"
                                                            title="Edit Author Name"
                                                            onClick={() => {
                                                                setEditingNameId(m.id);
                                                                setEditedName(m.name || "");
                                                            }}
                                                        ></i>
                                                    </>
                                                )}
                                            </div>


                                            <div className="article-impact-body-address">
                                                <p>{m.address}</p>
                                            </div>
                                        </div>

                                        <div className="article-impact-card-footer">
                                            <select
                                                className={`status-dropdown ${getStatusClass(m.statusOptions || 'Under Review')}`}
                                                value={m.statusOptions || 'Under Review'}
                                                onChange={(e) => handleStatusChange(e, card)}
                                            >
                                                {statusOptions.map((status) => (
                                                    <option key={status} value={status}>{status}</option>
                                                ))}
                                            </select>

                                            {(m.statusOptions || 'Under Review') === 'Published' && (
                                                <div className="certificate-upload-wrapper">

                                                    {!m.certificate && !uploadedFiles[globalIndex] && (
                                                        <button
                                                            className="upload-certificate-btn"
                                                            onClick={() => document.getElementById(`upload-input-${globalIndex}`).click()}
                                                            disabled={loading}
                                                        >
                                                            {loading ? (
                                                                <span className="spinner"></span>
                                                            ) : (
                                                                "Upload Certificate"
                                                            )}
                                                        </button>
                                                    )}

                                                    <input
                                                        type="file"
                                                        id={`upload-input-${globalIndex}`}
                                                        style={{ display: 'none' }}
                                                        accept="application/pdf"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                const formData = new FormData();
                                                                formData.append("certificate", file);

                                                                fetch(`https://backend.impactjournal.co.in/manuscript/upload-certificate/${m.referenceNumber}`, {
                                                                    method: "POST",
                                                                    body: formData,
                                                                })
                                                                    .then(async (res) => {
                                                                        const result = await res.json();
                                                                        if (res.ok) {
                                                                            toast.success("Certificate uploaded successfully");
                                                                            const updated = [...cards];
                                                                            if (updated[globalIndex]) {
                                                                                updated[globalIndex] = {
                                                                                    ...updated[globalIndex],
                                                                                    manuscriptDetails: {
                                                                                        ...updated[globalIndex].manuscriptDetails,
                                                                                        certificate: result.file
                                                                                    }
                                                                                };
                                                                            }
                                                                            setCards(updated);
                                                                        } else {
                                                                            toast.error(result.message || "Upload failed");
                                                                        }
                                                                    })
                                                                    .catch(() => {
                                                                        toast.error("Server error while uploading certificate");
                                                                    });
                                                            }
                                                        }}
                                                    />

                                                    {(m.certificate || uploadedFiles[globalIndex]) && (
                                                        <div className="uploaded-file-info">
                                                            <span
                                                                className="file-name"
                                                                onClick={() => {
                                                                    const fileName = m.certificate || uploadedFiles[globalIndex]?.name;
                                                                    const fileUrl = m.certificate
                                                                        ? `https://backend.impactjournal.co.in/uploads/manuscripts/${m.certificate}`
                                                                        : URL.createObjectURL(uploadedFiles[globalIndex].file);
                                                                    const link = document.createElement('a');
                                                                    link.href = fileUrl;
                                                                    link.download = fileName;
                                                                    link.target = '_blank';
                                                                    link.click();
                                                                }}
                                                            >
                                                                {(m.certificate || uploadedFiles[globalIndex]?.name || '')
                                                                    .toString()
                                                                    .slice(0, 20)}...
                                                            </span>
                                                            <Trash2
                                                                size={12}
                                                                color="red"
                                                                className="delete-icon"
                                                                onClick={() => {
                                                                    setSelectedArticle(card);
                                                                    setFileToDeleteIndex(globalIndex);
                                                                    setIsDeleteConfirmOpen(true);
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="article-impact-card-footer">
                                            {/* Volume Select */}
                                            <Select
                                                className="react-select-dropdown"
                                                options={volumeOptions}
                                                value={
                                                    volumeOptions.find(opt => opt.value === m.volume_id) ||
                                                    volumeOptions.find(opt => opt.label === m.volume) ||
                                                    null
                                                }
                                                onChange={(selectedOption) => {
                                                    const updated = [...cards];
                                                    if (updated[globalIndex]) {
                                                        updated[globalIndex] = {
                                                            ...updated[globalIndex],
                                                            manuscriptDetails: {
                                                                ...updated[globalIndex].manuscriptDetails,
                                                                volume_id: selectedOption.value,
                                                                volume: selectedOption.label
                                                            },
                                                        };
                                                        setCards(updated);

                                                        fetch(`https://backend.impactjournal.co.in/manuscript/updatearticle/${m.id}`, {
                                                            method: "PUT",
                                                            headers: { "Content-Type": "application/json" },
                                                            body: JSON.stringify({ volume_id: selectedOption.value }),
                                                        }).catch(() => { });
                                                    }
                                                }}
                                                placeholder="Select Volume"
                                            />

                                            {/* Issue Select */}
                                            <Select
                                                className="react-select-dropdown"
                                                options={issueOptions}
                                                value={
                                                    issueOptions.find(opt => opt.value === m.issue_id) ||
                                                    issueOptions.find(opt => opt.label === m.issue) ||
                                                    null
                                                }
                                                onChange={(selectedOption) => {
                                                    const updated = [...cards];
                                                    if (updated[globalIndex]) {
                                                        updated[globalIndex] = {
                                                            ...updated[globalIndex],
                                                            manuscriptDetails: {
                                                                ...updated[globalIndex].manuscriptDetails,
                                                                issue_id: selectedOption.value,
                                                                issue: selectedOption.label
                                                            },
                                                        };
                                                        setCards(updated);

                                                        fetch(`https://backend.impactjournal.co.in/manuscript/updatearticle/${m.id}`, {
                                                            method: "PUT",
                                                            headers: { "Content-Type": "application/json" },
                                                            body: JSON.stringify({ issue_id: selectedOption.value }),
                                                        }).catch(() => { });
                                                    }
                                                }}
                                                placeholder="Select Issue"
                                            />

                                            <button
                                                className="delete-article-btn"
                                                onClick={() => {
                                                    setArticleToDelete(card); // store manuscriptDetails
                                                    setIsArticleDeleteOpen(true);
                                                }}
                                            >
                                                <Trash2 size={14} color="red" /> Delete
                                            </button>

                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="pagination">
                        <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                            <i className="fa-solid fa-chevron-left"></i>
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>

                        <button className="active">
                            {currentPage}
                        </button>

                        {currentPage < totalPages && (
                            <button onClick={() => paginate(currentPage + 1)}>
                                {currentPage + 1}
                            </button>
                        )}

                        {currentPage + 1 < totalPages && (
                            <span className="dots">...</span>
                        )}

                        {currentPage !== totalPages && (
                            <button onClick={() => paginate(totalPages)}>
                                {totalPages}
                            </button>
                        )}

                        <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                            <i className="fa-solid fa-chevron-right"></i>
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            {isModalOpen && selectedArticle && selectedArticle.manuscriptDetails && (
                <div className={`all-article-modal-overlay-impact all-article-${(selectedArticle.manuscriptDetails.articleTitle || '')
                    .replace(/\s+/g, '-')
                    .toLowerCase()}-impact`}>
                    <div className="all-article-modal-content-impact">
                        <button className="all-article-close-modal-impact" onClick={closeModal}>×</button>
                        <h2>Article Details</h2>
                        <ul>
                            <li><strong>Reference Number:</strong> {selectedArticle.manuscriptDetails.referenceNumber}</li>
                            <li><strong>Title:</strong> {selectedArticle.title || selectedArticle.manuscriptDetails.articleTitle}</li>
                            <li><strong>Author:</strong> {selectedArticle.author || selectedArticle.manuscriptDetails.name}</li>
                            <li><strong>Category:</strong> {selectedArticle.category || selectedArticle.manuscriptDetails.articleCategory}</li>
                            <li><strong>Amount:</strong> ₹ {selectedArticle.fee}</li>
                            <li><strong>Payment Status:</strong> {selectedArticle.manuscriptDetails.pay_status}</li>
                            <li><strong>Address:</strong> {selectedArticle.manuscriptDetails.address}</li>
                            <li><strong>Email:</strong> {selectedArticle.manuscriptDetails.email}</li>
                            <li><strong>Mobile:</strong> {selectedArticle.manuscriptDetails.phone}</li>
                            <li>
                                <strong>Download File:</strong>{" "}
                                {selectedArticle.manuscriptDetails.file
                                    ? selectedArticle.manuscriptDetails.file.split('/').pop()
                                    : "N/A"}
                            </li>
                        </ul>
                    </div>
                </div>
            )}

            {/* Delete Certificate Confirm */}
            {isDeleteConfirmOpen && (
                <div className="account-modal-backdrop">
                    <div className="account-modal-content">
                        <h3>Confirm Certificate Delete</h3>
                        <p>Are you sure you want to delete this file?</p>
                        <div className="account-modal-buttons">
                            <button className="account-confirm-btn" onClick={confirmFileDelete}>Yes, Delete</button>
                            <button className="account-cancel-btn" onClick={cancelFileDelete}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Download Confirm */}
            {isDownloadConfirmOpen && (
                <div className="account-modal-backdrop">
                    <div className="account-modal-content">
                        <h3>Confirm Excel Download</h3>
                        <p>Do you want to download <strong>all</strong> records?</p>
                        <div className="account-modal-buttons">
                            <button
                                className="account-confirm-btn"
                                onClick={() => {
                                    if (pendingDownloadData) {
                                        downloadExcel(pendingDownloadData);
                                    }
                                    setIsDownloadConfirmOpen(false);
                                    setPendingDownloadData(null);
                                }}
                            >
                                Yes, Download
                            </button>

                            <button
                                className="account-cancel-btn"
                                onClick={() => {
                                    setIsDownloadConfirmOpen(false);
                                    setPendingDownloadData(null);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isArticleDeleteOpen && articleToDelete && (
                <div className="account-modal-backdrop">
                    <div className="account-modal-content">
                        <h3>Confirm Delete</h3>
                        <p>Are you sure you want to delete this manuscript?</p>

                        <div className="account-modal-buttons">
                            <button
                                className="account-confirm-btn"
                                onClick={deleteArticle}
                            >
                                Yes, Delete
                            </button>

                            <button
                                className="account-cancel-btn"
                                onClick={() => {
                                    setIsArticleDeleteOpen(false);
                                    setArticleToDelete(null);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}


            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={confirmStatusChange}
                newStatus={pendingStatus}
            />
        </>
    );
};

export default Articles;

const ConfirmationModal = ({ isOpen, onClose, onConfirm, newStatus }) => {
    if (!isOpen) return null;

    return (
        <div className="account-modal-backdrop">
            <div className="account-modal-content">
                <h3>Confirm Status Change</h3>
                <p>Are you sure you want to change the status to <strong>{newStatus}</strong>?</p>
                <div className="account-modal-buttons">
                    <button className="account-confirm-btn" onClick={onConfirm}>Yes, Change</button>
                    <button className="account-cancel-btn" onClick={onClose}>Cancel</button>
                </div>
            </div>
        </div>
    );
};
